import { Shift } from '../../services/rosterService';
import { startOfWeek, endOfWeek, eachDayOfInterval, format, isToday, isWeekend } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getStaffShiftLabel } from '../../utils/rosterStyles';

interface WeeklyRosterProps {
    shifts: Shift[];
    currentDate: Date;
    onNavigate: (direction: -1 | 1) => void;
}

const WeeklyRoster = ({ shifts, currentDate, onNavigate }: WeeklyRosterProps) => {
    const days = eachDayOfInterval({
        start: startOfWeek(currentDate, { weekStartsOn: 1 }),
        end: endOfWeek(currentDate, { weekStartsOn: 1 }),
    });

    // Group shifts by user
    const usersMap = new Map<string, { name: string; shifts: Shift[] }>();
    shifts.forEach((shift) => {
        if (!usersMap.has(shift.userId)) {
            usersMap.set(shift.userId, { name: shift.user.name, shifts: [] });
        }
        usersMap.get(shift.userId)!.shifts.push(shift);
    });

    const getShiftColor = (label: string) => {
        switch (label) {
            case 'LGW':
                return 'bg-blue-100 border-blue-200 text-blue-900';
            case 'CLEARER':
                return 'bg-sky-100 border-sky-200 text-sky-900';
            case 'LHR':
                return 'bg-rose-100 border-rose-200 text-rose-900';
            case 'CONTROL':
                return 'bg-purple-100 border-purple-200 text-purple-900';
            case 'OFFICE':
                return 'bg-gray-100 border-gray-200 text-gray-900';
            case 'WAREHOUSE':
                return 'bg-yellow-100 border-yellow-200 text-yellow-900';
            default:
                return 'bg-gray-100 border-gray-200 text-gray-900';
        }
    };

    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });

    return (
        <div>
            {/* Week navigation header */}
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h2 className="text-lg font-bold text-slate-800">
                        Week of {format(weekStart, 'MMM d')} – {format(weekEnd, 'MMM d, yyyy')}
                    </h2>
                    <p className="text-xs text-slate-400 mt-0.5">
                        {usersMap.size} staff member{usersMap.size !== 1 ? 's' : ''}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => onNavigate(-1)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors text-slate-600"
                    >
                        <ChevronLeft className="w-4 h-4" /> Previous Week
                    </button>
                    <button
                        onClick={() => onNavigate(1)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors text-slate-600"
                    >
                        Next Week <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200">
                    <thead>
                        <tr className="bg-slate-50">
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider sticky left-0 bg-slate-50 z-10 min-w-[110px]">
                                Staff
                            </th>
                            {days.map((day) => {
                                const today = isToday(day);
                                const weekend = isWeekend(day);
                                return (
                                    <th
                                        key={day.toString()}
                                        className={`px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider min-w-[110px]
                                            ${today ? 'text-blue-600' : weekend ? 'text-slate-400' : 'text-slate-500'}`}
                                    >
                                        <div className={`inline-flex flex-col items-center gap-0.5`}>
                                            <span>{format(day, 'EEE')}</span>
                                            <span className={`w-6 h-6 flex items-center justify-center rounded-full text-sm font-bold
                                                ${today ? 'bg-blue-600 text-white' : ''}`}>
                                                {format(day, 'd')}
                                            </span>
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-200">
                        {Array.from(usersMap.entries()).map(([userId, userData]) => (
                            <tr key={userId} className="hover:bg-slate-50/60 transition-colors">
                                <td className="px-4 py-3 text-sm font-semibold text-slate-800 sticky left-0 bg-white z-10 border-r border-slate-200">
                                    {userData.name}
                                </td>
                                {days.map((day) => {
                                    const dayKey = format(day, 'yyyy-MM-dd');
                                    const weekend = isWeekend(day);
                                    const today = isToday(day);
                                    const dayShifts = userData.shifts.filter((s) => {
                                        const shiftDate = format(new Date(s.startTime), 'yyyy-MM-dd');
                                        return shiftDate === dayKey;
                                    });
                                    return (
                                        <td
                                            key={day.toString()}
                                            className={`px-2 py-2 text-sm align-top
                                                ${weekend ? 'bg-slate-50/70' : ''}
                                                ${today ? 'bg-blue-50/40' : ''}`}
                                        >
                                            <div className="space-y-1 min-h-[44px] flex flex-col justify-center">
                                                {dayShifts.map((shift) => {
                                                    const label = getStaffShiftLabel(shift);
                                                    return (
                                                        <div
                                                            key={shift.id}
                                                            className={`px-2 py-1.5 rounded-lg text-xs border ${getShiftColor(label)}`}
                                                        >
                                                            <div className="font-bold">{label}</div>
                                                            <div className="opacity-70 mt-0.5">
                                                                {format(new Date(shift.startTime), 'HH:mm')}–{format(new Date(shift.endTime), 'HH:mm')}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                {dayShifts.length === 0 && (
                                                    <span className="text-slate-300 text-xs block text-center">—</span>
                                                )}
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                        {usersMap.size === 0 && (
                            <tr>
                                <td colSpan={8} className="px-6 py-10 text-center text-sm text-slate-400">
                                    No shifts scheduled for this week
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default WeeklyRoster;
